import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useT, renderRich } from "@/context/LanguageContext";
import { createCheckout, validateVoucher } from "@/lib/api";
import { TESTIDS } from "@/constants/testIds";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useState } from "react";

const formatEUR = (n) => `€${n.toFixed(2)}`;

const CartDrawer = ({ onRequireAuth }) => {
  const { t } = useT();
  const cart = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  // Voucher state — local to the drawer, resets when the modal closes/reopens.
  const [voucherInput, setVoucherInput] = useState("");
  const [voucher, setVoucher] = useState(null); // { code, amount }
  const [voucherErr, setVoucherErr] = useState(null);
  const [voucherBusy, setVoucherBusy] = useState(false);

  const applyVoucher = async () => {
    const raw = voucherInput.trim();
    if (!raw) return;
    setVoucherBusy(true); setVoucherErr(null);
    try {
      const res = await validateVoucher(raw);
      setVoucher({ code: res.code, amount: Number(res.amount) || 0 });
      setVoucherInput("");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setVoucherErr(typeof detail === "string" ? detail : "Invalid code");
      setVoucher(null);
    } finally {
      setVoucherBusy(false);
    }
  };

  const removeVoucher = () => { setVoucher(null); setVoucherErr(null); };

  // Voucher deducts a flat EUR from the total, floored at €0.50 to match
  // the server-side rule and Stripe's minimum charge amount.
  const voucherDiscount = voucher ? Math.min(voucher.amount, Math.max(0, cart.total - 0.5)) : 0;
  const grandTotal = Math.max(0, cart.total - voucherDiscount);

  const checkout = async () => {
    if (!cart.items.length) return;
    setBusy(true); setErr(null);
    try {
      const payload = {
        items: cart.items.map((it) => ({
          product_id: it.productId,
          variant_name: it.variantName,
          quantity: it.quantity,
        })),
        bundle_codes: cart.bundleCodes,
        origin_url: window.location.origin,
        voucher_code: voucher?.code || null,
      };
      const { url } = await createCheckout(payload);
      window.location.href = url;
    } catch (e) {
      // Surface the backend `detail` verbatim so shoppers see actionable
      // messages (e.g. "Vestido Sevilha M is sold out — only 0 left") rather
      // than a generic "Checkout failed". Falls back to a friendly default
      // when the server returns nothing useful.
      const detail = e?.response?.data?.detail;
      let msg;
      if (Array.isArray(detail)) {
        msg = detail.map((d) => d?.msg || String(d)).join(" · ");
      } else if (typeof detail === "string" && detail.trim()) {
        msg = detail;
      } else if (e?.message && !/network/i.test(e.message)) {
        msg = e.message;
      } else {
        msg = t("cart.checkoutError") || "We couldn't start your checkout. Please try again in a moment.";
      }
      setErr(msg);
    } finally {
      setBusy(false);
    }
  };

  if (!cart.drawerOpen) return null;

  return (
    <div className="fixed inset-0 z-[110] bg-[#2C2C2C]/40 fade-in" onClick={() => cart.setDrawerOpen(false)}>
      <aside
        data-testid={TESTIDS.cartDrawer}
        onClick={(e) => e.stopPropagation()}
        className="absolute top-0 right-0 h-full w-full md:w-[460px] bg-[#F9F8F5] flex flex-col drawer-anim"
      >
        <header className="flex items-center justify-between px-6 py-5 border-b border-[#E3DFD7]">
          <div>
            <div className="font-typewriter text-[10px] tracking-[0.32em] uppercase text-[#6B6862]">{t("cart.eyebrow")}</div>
            <h3 className="font-serif-display text-2xl mt-0.5">{t("cart.title")}</h3>
          </div>
          <button data-testid={TESTIDS.cartDrawerClose} onClick={() => cart.setDrawerOpen(false)} className="text-2xl font-thin">✕</button>
        </header>

        {cart.items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8" data-testid={TESTIDS.cartEmpty}>
            <div className="font-serif-display text-3xl italic text-[#6B6862]">{t("cart.empty.title")}</div>
            <p className="mt-3 text-sm text-[#6B6862]">{t("cart.empty.body")}</p>
            <button onClick={() => { cart.setDrawerOpen(false); navigate("/collection/all"); }} className="btn-ink mt-8">{t("cart.browse")}</button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-6 py-4 divide-y divide-[#E3DFD7]">
              {cart.items.map((it, i) => (
                <div key={`${it.productId}-${it.variantName}-${i}`} data-testid={TESTIDS.cartItem(i)} className="flex gap-4 py-4">
                  <Link to={`/product/${it.slug}`} onClick={() => cart.setDrawerOpen(false)} className="w-20 h-28 bg-[#FFFFFF] overflow-hidden shrink-0">
                    <img src={it.image} alt={it.name} className="w-full h-full object-cover" />
                  </Link>
                  <div className="flex-1">
                    <div className="flex justify-between gap-2">
                      <Link to={`/product/${it.slug}`} onClick={() => cart.setDrawerOpen(false)} className="font-serif-display text-lg leading-tight">{it.name}</Link>
                      <div className="font-typewriter text-xs text-right">
                        <div className={it.originalPrice > it.price ? "text-[#C8361B]" : ""}>{formatEUR(it.price * it.quantity)}</div>
                        {it.originalPrice > it.price && (
                          <s data-testid={`cart-item-original-${i}`} className="opacity-50 text-[10px]">{formatEUR(it.originalPrice * it.quantity)}</s>
                        )}
                      </div>
                    </div>
                    <div className="mt-1 font-typewriter text-[10px] tracking-[0.22em] uppercase text-[#6B6862]">{it.variantName}</div>
                    {it.bundleKey && (
                      <div className="mt-1 inline-block bg-[#C97A63]/15 text-[#C97A63] font-typewriter text-[9px] px-2 py-0.5 tracking-[0.2em] uppercase">{t("cart.bundleLabel")}</div>
                    )}
                    <div className="mt-3 flex items-center justify-between">
                      <div className="stepper">
                        <button onClick={() => cart.updateQuantity(i, it.quantity - 1)}>−</button>
                        <span>{it.quantity}</span>
                        <button onClick={() => cart.updateQuantity(i, it.quantity + 1)}>+</button>
                      </div>
                      <button data-testid={TESTIDS.cartItemRemove(i)} onClick={() => cart.removeItem(i)} className="font-typewriter text-[10px] tracking-[0.22em] uppercase underline text-[#6B6862]">{t("cart.remove")}</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <footer className="border-t border-[#E3DFD7] px-6 py-6 space-y-3">
              {/* Gift card / voucher — flat EUR off before Stripe */}
              <div data-testid="cart-voucher-block" className="border border-[#E3DFD7] rounded-md p-3">
                <div className="font-typewriter text-[10px] tracking-[0.28em] uppercase text-[#6B6862] mb-2">
                  {t("cart.voucher.label")}
                </div>
                {voucher ? (
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div data-testid="cart-voucher-code" className="font-badge text-[11px] tracking-[0.18em]">{voucher.code}</div>
                      <div className="text-xs text-[#6B6862]">−{formatEUR(voucher.amount)}</div>
                    </div>
                    <button
                      data-testid="cart-voucher-remove"
                      onClick={removeVoucher}
                      className="font-typewriter text-[10px] tracking-[0.22em] uppercase underline text-[#6B6862] hover:text-[#241C18]"
                    >
                      {t("cart.voucher.remove")}
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      data-testid="cart-voucher-input"
                      value={voucherInput}
                      onChange={(e) => { setVoucherInput(e.target.value.toUpperCase()); setVoucherErr(null); }}
                      onKeyDown={(e) => e.key === "Enter" && applyVoucher()}
                      placeholder={t("cart.voucher.placeholder")}
                      className="flex-1 min-w-0 px-3 py-2 border border-[#E3DFD7] bg-white rounded outline-none focus:border-[var(--gl-orange)] text-sm font-typewriter tracking-[0.05em]"
                    />
                    <button
                      data-testid="cart-voucher-apply"
                      onClick={applyVoucher}
                      disabled={voucherBusy || !voucherInput.trim()}
                      className="font-badge text-[11px] tracking-[0.2em] uppercase bg-[#241C18] text-[#F9F8F5] px-4 py-2 rounded disabled:opacity-40"
                    >
                      {voucherBusy ? "…" : t("cart.voucher.apply")}
                    </button>
                  </div>
                )}
                {voucherErr && (
                  <div data-testid="cart-voucher-error" className="mt-2 text-xs text-[#8B2E14]">
                    {voucherErr}
                  </div>
                )}
              </div>

              <div className="flex justify-between text-sm font-sans-clean text-[#6B6862]">
                <span>{t("cart.subtotal")}</span>
                <span data-testid={TESTIDS.cartSubtotal} className="font-typewriter">{formatEUR(cart.subtotal)}</span>
              </div>
              {cart.hasBundle && (
                <div data-testid={TESTIDS.cartBundleLine} className="flex justify-between text-sm font-sans-clean text-[#C97A63]">
                  <span>{t("cart.bundle")}</span>
                  <span className="font-typewriter">−{formatEUR(cart.bundleDiscount)}</span>
                </div>
              )}
              {voucher && voucherDiscount > 0 && (
                <div data-testid="cart-voucher-line" className="flex justify-between text-sm font-sans-clean text-[var(--gl-orange-hover)]">
                  <span>{t("cart.voucher.line")} · {voucher.code}</span>
                  <span className="font-typewriter">−{formatEUR(voucherDiscount)}</span>
                </div>
              )}
              <div className="flex justify-between font-serif-display text-xl">
                <span>{t("cart.total")}</span>
                <span data-testid={TESTIDS.cartTotal}>{formatEUR(grandTotal)}</span>
              </div>
              {err && (
                <div
                  role="alert"
                  data-testid="cart-checkout-error"
                  className="text-sm rounded-md border border-[#E7C4B7] bg-[#FBECE5] text-[#8B2E14] px-3 py-2 flex items-start gap-2"
                >
                  <span aria-hidden className="mt-0.5">⚠</span>
                  <span className="leading-snug">{err}</span>
                </div>
              )}

              {!user && (
                <p className="text-[11px] text-[#6B6862] italic" data-testid="cart-guest-tip">
                  {renderRich(t("cart.signinTip"), {
                    link: (label) => <button onClick={() => { cart.setDrawerOpen(false); onRequireAuth?.(); }} className="underline">{label}</button>,
                  })}
                </p>
              )}
              <button
                data-testid={TESTIDS.cartCheckoutBtn}
                disabled={busy}
                onClick={checkout}
                className="btn-ink w-full"
              >
                {busy
                  ? t("cart.preparing")
                  : user
                  ? t("cart.checkout")
                  : t("cart.checkoutGuest")}
              </button>
              <p className="text-center font-typewriter text-[10px] tracking-[0.22em] uppercase text-[#6B6862]">{t("cart.stripeNote")}</p>
              <div className="flex justify-center pt-1"><LanguageSwitcher variant="footer" /></div>
            </footer>
          </>
        )}
      </aside>
    </div>
  );
};

export default CartDrawer;
